import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Compass, Sparkles, Crown, Quote, Star, Heart, Award, Leaf, DoorOpen, HeartHandshake, Flower2, Eye, Target, X, Check } from "lucide-react";
import api from "@/lib/api";
import PublicLayout from "@/components/layout/PublicLayout";

const PHOTO_URL = "https://customer-assets.emergentagent.com/job_ceo-transformacao/artifacts/i7clu6ld_IMG_0244.JPG.jpeg";

export default function Home() {
  const [settings, setSettings] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    document.title = "Delicadamente — Ana Paula Moraes";
    Promise.all([
      api.get("/settings").then((r) => r.data),
      api.get("/testimonials").then((r) => r.data),
      api.get("/courses/public").then((r) => r.data),
    ]).then(([s, t, c]) => {
      setSettings(s);
      setTestimonials(t || []);
      setCourses(c || []);
    });
  }, []);

  const headline = settings?.headline || "O relacionamento que você deseja começa na mulher que você decide se tornar.";
  const subheadline =
    settings?.subheadline ||
    "Um universo de transformação feminina para mulheres que desejam reposicionar sua identidade, desenvolver autonomia emocional e construir relacionamentos mais saudáveis.";
  const aboutText = settings?.about_text || "";
  const photo = settings?.photo_url || PHOTO_URL;

  // Helpers to parse editable settings
  const heroTagline = settings?.hero_tagline || "Ao entrar para o Delicadamente, além de se tornar magnética, você se torna a mulher impossível de ser ignorada.";
  const featuredPhrase = settings?.featured_phrase || "A primeira plataforma do Brasil que reúne feminilidade e mentalidade em um mesmo ambiente.";
  const manifestoLine1 = settings?.manifesto_line1 || "Algumas mulheres passam a vida tentando ser amadas.";
  const manifestoLine2 = settings?.manifesto_line2 || "Outras aprendem a se posicionar emocionalmente.";
  const manifestoPrefix = settings?.manifesto_prefix || "E isso muda completamente";
  const manifestoItems = (settings?.manifesto_items || "A forma como se relacionam\nO tipo de homem que atraem\nO que aceitam\nO que constroem\nA maneira como são percebidas").split("\n").map((s) => s.trim()).filter(Boolean);
  const manifestoBox = settings?.manifesto_box || "É um universo de reposicionamento feminino para mulheres que desejam desenvolver elegância emocional, presença e relações mais conscientes.";
  const aboutPhoto = settings?.about_photo_url || "https://customer-assets.emergentagent.com/job_ceo-transformacao/artifacts/7zby29g5_IMG_6104.jpeg";
  const aboutRole = settings?.about_role || "Economista, Advogada e Psicanalista.";
  const aboutP1 = settings?.about_p1 || "Mas, acima de tudo, uma mulher que escolheu transformar sua própria história para ajudar outras mulheres a fazerem o mesmo.";
  const aboutP2 = settings?.about_p2 || "Durante anos, estudei comportamento humano, relações e emoções. Na prática, vivi, observei e entendi o que livros e teorias não ensinam: sobre amor, autoestima, limites e o poder de se reposicionar.";
  const aboutP3 = settings?.about_p3 || "Hoje, dedico minha vida a ajudar mulheres a se posicionarem emocionalmente, fortalecerem sua identidade e construírem relações que realmente as valorizam.";
  const aboutQuote = settings?.about_quote || "Não se trata de mudar quem você é. É sobre se lembrar de quem você nasceu para ser.";
  const aboutClosing = settings?.about_closing || "O Delicadamente existe para transformar a forma como mulheres se relacionam consigo mesmas, com o amor e com a vida.";
  const mission = settings?.mission || "Desenvolver mulheres emocionalmente conscientes, femininas e posicionadas para viver relações mais saudáveis e uma vida mais alinhada com quem realmente são.";
  const vision = settings?.vision || "Ser a principal plataforma brasileira de reposicionamento feminino, unindo feminilidade, mentalidade e relacionamentos em uma experiência sofisticada de transformação emocional.";
  const valuesTitle = settings?.values_title || "Os pilares que sustentam o Delicadamente";
  const VALUE_ICONS = [Flower2, Heart, Crown, HeartHandshake, Sparkles, Compass];
  const valuesItems = (settings?.values_items || "Feminilidade Consciente|Acreditamos em uma feminilidade madura, autêntica e emocionalmente inteligente.\nPresença Emocional|Relacionamentos saudáveis começam na forma como uma mulher se posiciona emocionalmente.\nAutonomia sem Endurecimento|Uma mulher não precisa perder sua feminilidade para desenvolver força e independência.\nRelações Conscientes|Defendemos relações construídas com reciprocidade, comunicação e maturidade emocional.\nElegância Emocional|A forma como uma mulher sente, escolhe e se posiciona transforma completamente a forma como vive o amor.\nTransformação com Profundidade|Não acreditamos em fórmulas rasas. A verdadeira mudança acontece quando identidade, mentalidade e feminilidade caminham juntas.").split("\n").map((line, i) => {
    const [title, ...rest] = line.split("|");
    return { icon: VALUE_ICONS[i % VALUE_ICONS.length], title: (title || "").trim(), desc: rest.join("|").trim() };
  }).filter((v) => v.title);
  const methodTitle = settings?.method_title || "O que você vai e o que não vai encontrar aqui";
  const methodWontTeach = (settings?.method_wont_teach || "A manipular, controlar ou prender qualquer homem.\nTécnicas de sedução ou jogos emocionais.\nA mudar quem você é para agradar ou ser escolhida.\nFórmulas mágicas ou promessas vazias.\nA depender de validação masculina para se sentir suficiente.\nA se anular para manter um relacionamento.").split("\n").map((s) => s.trim()).filter(Boolean);
  const methodWillTeach = (settings?.method_will_teach || "A desenvolver sua feminilidade, presença e autenticidade.\nA fortalecer sua mentalidade e autonomia emocional.\nA se posicionar com clareza e segurança.\nA construir limites saudáveis e manter sua identidade.\nA escolher e construir relações recíprocas e conscientes.\nA se tornar a mulher impossível de ser ignorada.").split("\n").map((s) => s.trim()).filter(Boolean);
  const methodForYou = (settings?.method_for_you || "Você sente que se perdeu tentando ser forte ou tentando ser amada.\nVocê está cansada de se relacionar com homens indisponíveis.\nVocê quer se tornar uma mulher mais feminina, confiante e segura.\nVocê deseja construir um relacionamento saudável e duradouro.\nVocê está pronta para se escolher de verdade e transformar sua vida.").split("\n").map((s) => s.trim()).filter(Boolean);
  const methodNotForYou = (settings?.method_not_for_you || "Você não está disposta a olhar para dentro e fazer mudanças.\nVocê busca atalhos, fórmulas prontas ou soluções mágicas.\nVocê não aceita responsabilidade pela sua vida e suas escolhas.\nVocê não quer evoluir e prefere permanecer como está.\nVocê acredita que o problema sempre está no outro.").split("\n").map((s) => s.trim()).filter(Boolean);
  const methodClosing = settings?.method_closing || "Você não foi feita para competir, se anular ou correr atrás. Você nasceu para ser impossível de ser ignorada.";
  const ctaFinalTitle = settings?.cta_final_title || "Pronta para se tornar a mulher impossível de ser ignorada?";
  const ctaFinalDesc = settings?.cta_final_desc || "Entre para a área de membros e comece sua jornada de transformação emocional.";

  // Render headline with "mulher" in italic accent (first occurrence)
  const renderHeadline = (text) => {
    const idx = text.toLowerCase().indexOf("mulher");
    if (idx < 0) return text;
    return (
      <>
        {text.slice(0, idx)}
        <span className="headline-italic-accent">{text.slice(idx, idx + 6)}</span>
        {text.slice(idx + 6)}
      </>
    );
  };

  return (
    <PublicLayout>
      {/* ========== HERO ========== */}
      <section className="relative overflow-hidden">
        <div className="brand-blob absolute inset-0 -z-10" />

        {/* Decorative floating dots */}
        <div className="absolute top-32 left-8 w-1.5 h-1.5 rounded-full bg-[color:var(--copper)] opacity-60 hidden lg:block" />
        <div className="absolute top-72 left-20 w-1 h-1 rounded-full bg-[color:var(--rose-gold)] opacity-50 hidden lg:block" />
        <div className="absolute bottom-40 right-10 w-2 h-2 rounded-full bg-[color:var(--burnt-gold)] opacity-40 hidden lg:block" />

        <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-16 lg:pt-24 pb-24 lg:pb-40 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Left — text */}
          <div className="lg:col-span-7 fade-in-up">
            <div className="mb-6">
              <span className="font-serif italic text-3xl md:text-4xl" style={{ color: "var(--copper)" }}>
                Delicadamente
              </span>
            </div>
            <div className="flex items-center gap-3 mb-8">
              <span className="dot-ornament" />
              <span className="eyebrow">Transformação feminina · Identidade · Autonomia emocional</span>
            </div>

            <h1 className="headline-hero text-balance mb-8">
              {renderHeadline(headline)}
            </h1>

            <p className="body-lead max-w-xl mb-10 text-pretty">
              {subheadline}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <a href="#metodo" className="btn-primary" data-testid="hero-cta-metodo">
                Quero começar minha transformação agora <ArrowRight size={16} />
              </a>
              <Link to="/login" className="btn-secondary" data-testid="hero-cta-membros">
                Acessar área de membros
              </Link>
            </div>

            <div className="flex items-start gap-5">
              <div className="gold-line mt-3 flex-shrink-0" />
              <span className="font-display italic text-base md:text-lg leading-relaxed max-w-xl" style={{ color: "var(--copper)" }}>
                {heroTagline}
              </span>
            </div>
          </div>

          {/* Right — photo */}
          <div className="lg:col-span-5 relative slide-in-right">
            {/* soft shapes behind */}
            <div className="absolute -top-8 -left-6 w-40 h-40 rounded-full -z-10" style={{ background: "rgba(232, 217, 189, 0.5)" }} />
            <div className="absolute -bottom-10 -right-8 w-52 h-52 rounded-full -z-10" style={{ background: "rgba(220, 201, 166, 0.5)" }} />

            <div className="relative hero-photo-frame aspect-[4/5] max-w-md mx-auto">
              <img src={photo} alt="Ana Paula Moraes" className="w-full h-full object-cover" data-testid="hero-photo" />
            </div>

            {/* Floating badge */}
            <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 px-5 py-3 rounded-full shadow-xl flex items-center gap-2.5 whitespace-nowrap" style={{ background: "var(--surface)", border: "1px solid var(--border-light)" }}>
              <Heart size={13} style={{ color: "var(--rose-gold)" }} fill="currentColor" />
              <span className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: "var(--copper)" }}>
                Mentora · Transformação Feminina
              </span>
            </div>

            {/* Vertical caption */}
            <div className="hidden xl:flex absolute -right-4 top-1/2 -translate-y-1/2 flex-col items-center gap-3" style={{ writingMode: "vertical-rl" }}>
              <span className="serial-mark uppercase tracking-[0.32em] text-xs">Edição · 2026</span>
              <div className="w-px h-12 bg-[color:var(--copper)] opacity-40" />
            </div>
          </div>
        </div>

        {/* Trust strip */}
        <div className="border-t border-b" style={{ borderColor: "var(--border-light)" }}>
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6 flex items-center justify-center">
            <div className="flex items-center gap-2 sm:gap-4 text-[11px] sm:text-xs uppercase tracking-[0.28em] font-semibold text-center" style={{ color: "var(--text-muted)" }}>
              <span>Transformação Feminina</span>
              <span className="dot-ornament opacity-60 flex-shrink-0" />
              <span>Identidade</span>
              <span className="dot-ornament opacity-60 flex-shrink-0" />
              <span>Autonomia Emocional</span>
            </div>
          </div>
        </div>
      </section>

      {/* ========== HIGHLIGHT TAGLINE ========== */}
      <section className="relative py-12 md:py-16 overflow-hidden" style={{ background: "linear-gradient(135deg, #C9A97A 0%, #8A6F47 100%)" }}>
        <div className="absolute inset-0 grain-overlay opacity-50" />
        <div className="absolute top-0 left-1/4 w-px h-full bg-white/10" />
        <div className="absolute top-0 right-1/4 w-px h-full bg-white/10" />

        <div className="max-w-5xl mx-auto px-6 lg:px-12 text-center text-white relative">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Sparkles size={14} className="opacity-80" />
            <span className="text-[10px] uppercase tracking-[0.32em] font-semibold opacity-90">Inédito no Brasil</span>
            <Sparkles size={14} className="opacity-80" />
          </div>
          <p className="font-serif text-2xl md:text-3xl lg:text-[2.1rem] leading-[1.3] tracking-tight text-balance">
            {featuredPhrase}
          </p>
        </div>
      </section>

      {/* ========== MANIFESTO / POSICIONAMENTO ========== */}
      <section className="py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 fade-in-up">
          <div className="flex items-center justify-center gap-3 mb-12">
            <div className="w-10 h-px bg-[color:var(--copper)] opacity-50" />
            <span className="eyebrow">Manifesto</span>
            <div className="w-10 h-px bg-[color:var(--copper)] opacity-50" />
          </div>

          <div className="space-y-7 md:space-y-9 text-center">
            <p className="font-serif text-2xl md:text-3xl lg:text-4xl leading-[1.25] text-balance" style={{ color: "var(--text-primary)" }}>
              {manifestoLine1}
            </p>
            <p className="font-serif italic text-2xl md:text-3xl lg:text-4xl leading-[1.25] text-balance" style={{ color: "var(--copper)" }}>
              {manifestoLine2}
            </p>
          </div>

          <div className="my-14 md:my-20 text-center">
            <p className="text-sm md:text-base uppercase tracking-[0.32em] font-semibold mb-10" style={{ color: "var(--copper)" }}>
              {manifestoPrefix}
            </p>

            <ul className="inline-flex flex-col gap-4 text-left">
              {manifestoItems.map((item, i) => (
                <li key={i} className="flex items-baseline gap-3">
                  <span className="dot-ornament flex-shrink-0 translate-y-[2px]" />
                  <span className="font-serif text-lg md:text-xl leading-snug" style={{ color: "var(--text-primary)" }}>
                    {item}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div className="relative px-6 md:px-12 py-10 md:py-14 text-center" style={{ background: "rgba(201, 169, 122, 0.08)", border: "1px solid var(--border-light)", borderRadius: "1.5rem" }}>
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ background: "var(--bg)", color: "var(--copper)", border: "1px solid var(--border-light)" }}>
              O Delicadamente
            </div>
            <p className="font-serif text-xl md:text-2xl lg:text-[1.7rem] leading-[1.4] text-balance" style={{ color: "var(--text-primary)" }}>
              {manifestoBox}
            </p>
          </div>
        </div>
      </section>

      {/* ========== SOBRE / QUEM É ANA PAULA MORAES ========== */}
      <section className="py-24 md:py-32" id="sobre">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {/* Intro split */}
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start mb-20 md:mb-28">
            <div className="lg:col-span-5 fade-in-up lg:sticky lg:top-28">
              <div className="img-reveal aspect-[4/5]">
                <img
                  src={aboutPhoto}
                  alt="Ana Paula Moraes"
                />
              </div>
              <div className="mt-6 flex items-center gap-3">
                <span className="serial-mark">N° 01</span>
                <div className="flex-1 h-px bg-[color:var(--copper)] opacity-30" />
                <span className="serial-mark uppercase tracking-[0.28em] text-[11px]">Sobre</span>
              </div>
            </div>

            <div className="lg:col-span-7 fade-in-up delay-100">
              <div className="divider-ornament eyebrow mb-6">{settings?.about_title || "Quem é Ana Paula Moraes"}</div>
              <h2 className="font-serif text-balance mb-3 leading-[1.05] tracking-tight" style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)", color: "var(--text-primary)" }}>
                Prazer, eu sou{" "}
                <span className="headline-italic-accent">Ana Paula Moraes.</span>
              </h2>
              <p className="text-[11px] sm:text-xs uppercase tracking-[0.32em] font-semibold mb-8" style={{ color: "var(--copper)" }}>
                {aboutRole}
              </p>

              <p className="body-lead mb-6 text-pretty">{aboutP1}</p>
              <p className="body-lead mb-6 text-pretty">{aboutP2}</p>
              <p className="body-lead mb-10 text-pretty">{aboutP3}</p>

              <div className="relative p-8 md:p-10 mb-10" style={{ background: "rgba(201, 169, 122, 0.10)", border: "1px solid var(--border-light)", borderRadius: "1.5rem" }}>
                <Quote size={26} style={{ color: "var(--rose-gold)" }} className="mb-4 opacity-60" />
                <p className="font-serif italic text-xl md:text-2xl leading-snug text-balance" style={{ color: "var(--text-primary)" }}>
                  {aboutQuote}
                </p>
              </div>

              <Link to="/sobre" className="btn-secondary" data-testid="about-cta">
                Conheça minha história <ArrowRight size={16} />
              </Link>
            </div>
          </div>

          {/* Missão & Visão */}
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8 mb-16 md:mb-20">
            {[
              { label: "Missão", icon: Target, text: mission },
              { label: "Visão", icon: Eye, text: vision },
            ].map((b, i) => (
              <div key={b.label} className="brand-card p-8 md:p-10 fade-in-up" style={{ animationDelay: `${i * 120}ms` }}>
                <div className="flex items-center justify-between mb-6">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: "rgba(201, 169, 122, 0.12)" }}>
                    <b.icon size={20} strokeWidth={1.5} style={{ color: "var(--copper)" }} />
                  </div>
                  <span className="font-display italic text-xl" style={{ color: "var(--copper)" }}>{i === 0 ? "I" : "II"}</span>
                </div>
                <div className="eyebrow mb-3">{b.label}</div>
                <p className="text-base md:text-lg leading-relaxed" style={{ color: "var(--text-primary)" }}>{b.text}</p>
              </div>
            ))}
          </div>

          {/* Nossos Valores */}
          <div className="mb-12">
            <div className="text-center mb-12 md:mb-16">
              <div className="flex items-center justify-center gap-3 mb-5">
                <div className="w-10 h-px bg-[color:var(--copper)] opacity-50" />
                <span className="eyebrow">Nossos valores</span>
                <div className="w-10 h-px bg-[color:var(--copper)] opacity-50" />
              </div>
              <h3 className="font-serif text-2xl md:text-3xl lg:text-4xl tracking-tight" style={{ color: "var(--text-primary)" }}>
                {valuesTitle}
              </h3>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
              {valuesItems.map((v, i) => (
                <div key={v.title} className="brand-card p-7 lg:p-8 fade-in-up" style={{ animationDelay: `${i * 70}ms` }}>
                  <div className="w-11 h-11 rounded-full flex items-center justify-center mb-5" style={{ background: "rgba(201, 169, 122, 0.12)" }}>
                    <v.icon size={18} strokeWidth={1.5} style={{ color: "var(--copper)" }} />
                  </div>
                  <h4 className="font-serif text-lg md:text-xl mb-3 leading-tight" style={{ color: "var(--text-primary)" }}>{v.title}</h4>
                  <p className="text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>{v.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Closing line */}
          <div className="text-center max-w-3xl mx-auto pt-8">
            <p className="font-serif italic text-xl md:text-2xl lg:text-[1.7rem] leading-[1.45] text-balance" style={{ color: "var(--text-primary)" }}>
              {aboutClosing}
            </p>
          </div>
        </div>
      </section>

      {/* ========== PILARES ========== */}
      <section className="py-24 md:py-32 relative overflow-hidden" style={{ background: "var(--bg-warm)" }}>
        {/* Background woman image with gradient fade */}
        <div
          className="absolute top-0 right-0 bottom-0 w-full lg:w-[70%] pointer-events-none"
          style={{
            backgroundImage: "url('https://customer-assets.emergentagent.com/job_ceo-transformacao/artifacts/e3712h5c_image.png')",
            backgroundSize: "cover",
            backgroundPosition: "65% 25%",
            backgroundRepeat: "no-repeat",
            maskImage: "linear-gradient(to left, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.85) 25%, rgba(0,0,0,0.5) 50%, rgba(0,0,0,0.15) 75%, transparent 95%)",
            WebkitMaskImage: "linear-gradient(to left, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.85) 25%, rgba(0,0,0,0.5) 50%, rgba(0,0,0,0.15) 75%, transparent 95%)",
          }}
        />
        {/* Warm sepia tint to harmonize photo with cream palette */}
        <div className="absolute inset-0 pointer-events-none" style={{ background: "rgba(238, 223, 194, 0.18)", mixBlendMode: "color" }} />
        {/* Bottom fade for card readability */}
        <div
          className="absolute left-0 right-0 bottom-0 h-[55%] pointer-events-none"
          style={{
            background: "linear-gradient(180deg, transparent 0%, rgba(238, 223, 194, 0.85) 40%, var(--bg-warm) 70%, var(--bg-warm) 100%)",
          }}
        />

        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative">
          {/* Title */}
          <div className="max-w-2xl mb-16 md:mb-20 fade-in-up">
            <div
              className="inline-block px-6 py-5 sm:px-8 sm:py-7 md:px-10 md:py-8 rounded-2xl"
              style={{
                background: "rgba(251, 244, 228, 0.82)",
                backdropFilter: "blur(10px)",
                WebkitBackdropFilter: "blur(10px)",
                boxShadow: "0 0 60px 20px rgba(251, 244, 228, 0.6)",
              }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-px bg-[color:var(--copper)] opacity-50" />
                <span className="eyebrow">O Delicadamente</span>
              </div>
              <h2 className="font-serif text-balance text-[1.45rem] sm:text-2xl md:text-[1.85rem] lg:text-[2.15rem] leading-[1.45] tracking-tight" style={{ color: "var(--text-primary)" }}>
                Algumas mulheres aprenderam a ser fortes, outras se perderam tentando ser amadas,{" "}
                <span className="font-serif italic" style={{ color: "var(--copper)" }}>
                  mas você nasceu para ser impossível de ser ignorada.
                </span>
              </h2>
              <div className="mt-8 w-32 h-px" style={{ background: "linear-gradient(90deg, var(--copper), transparent)" }} />
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {[
              { icon: DoorOpen, n: "01", title: "Como parar de atrair homens indisponíveis", desc: "Reconheça padrões, fortaleça sua autoestima e deixe de se envolver com relações que não te escolhem." },
              { icon: Sparkles, n: "02", title: "Como se tornar a mulher impossível de ser ignorada", desc: "Desenvolva sua presença, feminilidade e magnetismo natural e se torne uma mulher que se destaca sem esforço." },
              { icon: HeartHandshake, n: "03", title: "Como construir e manter um relacionamento saudável", desc: "Aprenda a se relacionar com consciência, comunicação e reciprocidade, construindo uma relação leve e duradoura." },
            ].map((p, i) => (
              <div key={p.title} className="relative pt-8 fade-in-up" style={{ animationDelay: `${i * 140}ms` }}>
                {/* Number badge */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 z-10">
                  <div className="w-14 h-14 rounded-full flex items-center justify-center shadow-md" style={{ background: "#7A3F44", color: "white" }}>
                    <span className="font-serif text-base tracking-wider">{p.n}</span>
                  </div>
                </div>

                <div className="brand-card-flat p-8 lg:p-10 pt-12 text-center h-full flex flex-col" style={{ background: "var(--surface)", border: "1px solid var(--border-light)", boxShadow: "0 4px 20px -8px rgba(43, 26, 21, 0.08)" }}>
                  <h3 className="font-serif text-lg md:text-xl mb-5 leading-tight text-balance" style={{ color: "var(--text-primary)" }}>
                    {p.title}
                  </h3>

                  {/* Diamond separator */}
                  <div className="flex items-center justify-center gap-2 my-4">
                    <div className="w-8 h-px" style={{ background: "var(--copper)", opacity: 0.4 }} />
                    <span style={{ color: "var(--copper)", opacity: 0.7, fontSize: "8px" }}>◆</span>
                    <div className="w-8 h-px" style={{ background: "var(--copper)", opacity: 0.4 }} />
                  </div>

                  {/* Icon circle */}
                  <div className="my-6 flex justify-center">
                    <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{ border: "1px solid var(--copper)", background: "rgba(201, 169, 122, 0.04)" }}>
                      <p.icon size={28} strokeWidth={1.3} style={{ color: "var(--copper)" }} />
                    </div>
                  </div>

                  <p className="text-sm leading-relaxed mt-auto" style={{ color: "var(--text-secondary)" }}>
                    {p.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom badges row */}
          <div className="mt-16 px-6 py-5 rounded-2xl flex flex-col sm:flex-row items-center justify-around gap-4 sm:gap-2" style={{ background: "var(--surface)", border: "1px solid var(--border-light)" }}>
            {[
              { icon: Sparkles, label: "Mentalidade que transforma" },
              { icon: Flower2, label: "Feminilidade que atrai" },
              { icon: Heart, label: "Relacionamentos que constroem" },
            ].map((b, i) => (
              <div key={i} className="flex items-center gap-3">
                <b.icon size={16} style={{ color: "var(--copper)" }} strokeWidth={1.5} />
                <span className="text-[11px] sm:text-xs uppercase tracking-[0.24em] font-semibold" style={{ color: "var(--text-secondary)" }}>
                  {b.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== MÉTODO ========== */}
      <section id="metodo" className="py-28 md:py-40 warm-bg">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16 md:mb-20 fade-in-up">
            <div className="flex items-center justify-center gap-3 mb-5">
              <div className="w-10 h-px bg-[color:var(--copper)] opacity-50" />
              <span className="eyebrow">O método</span>
              <div className="w-10 h-px bg-[color:var(--copper)] opacity-50" />
            </div>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.1] text-balance max-w-3xl mx-auto" style={{ color: "var(--text-primary)" }}>
              {methodTitle}
            </h2>
          </div>

          {/* Eu NÃO vou te ensinar / Eu VOU te ensinar */}
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8 mb-16 md:mb-20">
            {/* NÃO */}
            <div className="brand-card-flat p-8 md:p-10 fade-in-up" style={{ background: "var(--surface)", border: "1px solid var(--border-light)" }}>
              <div className="flex items-center gap-3 mb-7">
                <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: "rgba(220, 38, 38, 0.08)" }}>
                  <X size={20} strokeWidth={2.2} style={{ color: "#B91C1C" }} />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: "#B91C1C" }}>Aqui você não vai</div>
                  <h3 className="font-serif text-xl md:text-2xl" style={{ color: "var(--text-primary)" }}>Eu não vou te ensinar</h3>
                </div>
              </div>
              <ul className="space-y-3">
                {methodWontTeach.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <X size={14} strokeWidth={2.5} className="flex-shrink-0 mt-1.5" style={{ color: "#B91C1C" }} />
                    <span className="text-[15px] leading-relaxed" style={{ color: "var(--text-primary)" }}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* SIM */}
            <div className="brand-card-flat p-8 md:p-10 fade-in-up delay-100" style={{ background: "var(--surface)", border: "1px solid var(--copper)", boxShadow: "0 8px 30px -10px rgba(184, 153, 104, 0.3)" }}>
              <div className="flex items-center gap-3 mb-7">
                <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: "rgba(201, 169, 122, 0.18)" }}>
                  <Check size={20} strokeWidth={2.2} style={{ color: "var(--copper)" }} />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: "var(--copper)" }}>Aqui você vai</div>
                  <h3 className="font-serif text-xl md:text-2xl" style={{ color: "var(--text-primary)" }}>Eu vou te ensinar</h3>
                </div>
              </div>
              <ul className="space-y-3">
                {methodWillTeach.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check size={14} strokeWidth={2.5} className="flex-shrink-0 mt-1.5" style={{ color: "var(--copper)" }} />
                    <span className="text-[15px] leading-relaxed" style={{ color: "var(--text-primary)" }}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Para quem é / Não é */}
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8 mb-16">
            {/* PARA VOCÊ */}
            <div className="brand-card-flat p-8 md:p-10 fade-in-up" style={{ background: "var(--surface)", border: "1px solid var(--border-light)" }}>
              <div className="flex items-center gap-3 mb-7">
                <Heart size={22} fill="currentColor" style={{ color: "#C9A97A" }} />
                <h3 className="font-serif text-lg md:text-xl tracking-tight" style={{ color: "var(--text-primary)" }}>
                  Essa mentoria é para você se...
                </h3>
              </div>
              <ul className="space-y-4">
                {methodForYou.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Heart size={11} fill="currentColor" className="flex-shrink-0 mt-2" style={{ color: "#C9A97A" }} />
                    <span className="text-[15px] leading-relaxed" style={{ color: "var(--text-primary)" }}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* NÃO PARA VOCÊ */}
            <div className="brand-card-flat p-8 md:p-10 fade-in-up delay-100" style={{ background: "var(--surface)", border: "1px solid var(--border-light)" }}>
              <div className="flex items-center gap-3 mb-7">
                <Heart size={22} style={{ color: "#B91C1C" }} fill="currentColor" />
                <h3 className="font-serif text-lg md:text-xl tracking-tight" style={{ color: "var(--text-primary)" }}>
                  Essa mentoria não é para você se...
                </h3>
              </div>
              <ul className="space-y-4">
                {methodNotForYou.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <X size={13} strokeWidth={2.5} className="flex-shrink-0 mt-1.5" style={{ color: "#B91C1C" }} />
                    <span className="text-[15px] leading-relaxed" style={{ color: "var(--text-primary)" }}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Closing quote */}
          <div className="text-center max-w-3xl mx-auto pt-10">
            <p className="font-serif text-xl md:text-2xl lg:text-[1.7rem] leading-[1.4] text-balance" style={{ color: "var(--text-primary)" }}>
              {methodClosing}
            </p>
          </div>
        </div>
      </section>

      {/* ========== CONTEÚDOS ========== */}
      <section className="py-28 md:py-40 warm-bg">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16 gap-8">
            <div className="max-w-2xl">
              <div className="divider-ornament eyebrow mb-6">Conteúdos & jornadas</div>
              <h2 className="headline-section text-balance">
                Conteúdos para{" "}
                <span className="headline-italic-accent">transformar</span>{" "}
                sua vida
              </h2>
            </div>
            <Link to="/conteudos" className="btn-ghost-link self-start md:self-end" data-testid="see-all-courses">
              Ver todos os conteúdos <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {courses.slice(0, 3).map((c, i) => {
              const hasExternal = !!c.external_url;
              const cardInner = (
                <>
                  <div className="aspect-[4/3] overflow-hidden relative">
                    <img src={c.cover_url} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[rgba(43,26,21,0.4)] via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  </div>
                  <div className="p-7 lg:p-8">
                    <div className="flex items-center gap-2 mb-4">
                      <span className="serial-mark text-xs">N° {String(i + 1).padStart(2, '0')}</span>
                      <span className="dot-ornament opacity-40" />
                      <span className="text-[10px] uppercase tracking-[0.24em] font-semibold" style={{ color: "var(--copper)" }}>Curso</span>
                    </div>
                    <h3 className="font-serif text-xl md:text-2xl mb-3 leading-tight" style={{ color: "var(--text-primary)" }}>{c.title}</h3>
                    <p className="text-sm leading-relaxed mb-5 line-clamp-3" style={{ color: "var(--text-secondary)" }}>{c.description}</p>
                    <span className="btn-ghost-link">
                      {hasExternal ? "Quero participar" : "Ver detalhes"} <ArrowRight size={14} />
                    </span>
                  </div>
                </>
              );
              return hasExternal ? (
                <a
                  href={c.external_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  key={c.id}
                  className="brand-card overflow-hidden block group fade-in-up"
                  style={{ animationDelay: `${i * 120}ms` }}
                  data-testid={`course-card-${i}`}
                >
                  {cardInner}
                </a>
              ) : (
                <Link
                  to="/login"
                  key={c.id}
                  className="brand-card overflow-hidden block group fade-in-up"
                  style={{ animationDelay: `${i * 120}ms` }}
                  data-testid={`course-card-${i}`}
                >
                  {cardInner}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ========== DEPOIMENTOS ========== */}
      <section id="depoimentos" className="py-28 md:py-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-20 max-w-3xl mx-auto">
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="w-10 h-px bg-[color:var(--copper)] opacity-50" />
              <span className="eyebrow">Histórias reais</span>
              <div className="w-10 h-px bg-[color:var(--copper)] opacity-50" />
            </div>
            <h2 className="headline-section text-balance">
              Mulheres que assumiram o{" "}
              <span className="headline-italic-accent">papel principal</span>.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {testimonials.slice(0, 3).map((t, i) => (
              <div key={t.id} className="brand-card p-8 lg:p-10 fade-in-up flex flex-col" style={{ animationDelay: `${i * 140}ms` }} data-testid={`testimonial-${i}`}>
                <span className="quote-mark mb-4 self-start">&ldquo;</span>
                <p className="text-base leading-relaxed mb-8 flex-1" style={{ color: "var(--text-primary)" }}>{t.text}</p>
                <div className="flex items-center justify-between pt-6 border-t" style={{ borderColor: "var(--border-light)" }}>
                  <div>
                    <div className="font-serif text-lg mb-0.5" style={{ color: "var(--text-primary)" }}>{t.name}</div>
                    <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--text-muted)" }}>Aluna</div>
                  </div>
                  <div className="stars-row">
                    {Array.from({ length: t.rating || 5 }).map((_, j) => (
                      <Star key={j} size={13} fill="currentColor" />
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== CTA FINAL ========== */}
      <section className="relative overflow-hidden py-28 md:py-40">
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, #C48F95 0%, #B87333 100%)" }} />
        <div className="absolute inset-0 grain-overlay" />
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full opacity-20" style={{ background: "radial-gradient(circle, white, transparent 70%)" }} />
        <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full opacity-15" style={{ background: "radial-gradient(circle, white, transparent 70%)" }} />

        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center text-white relative">
          <Crown size={32} className="mx-auto mb-8 opacity-90" strokeWidth={1.5} />
          <div className="text-[11px] uppercase tracking-[0.32em] font-semibold opacity-80 mb-6">
            O Convite
          </div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-8 leading-[1.05] tracking-tight text-balance">
            {ctaFinalTitle}
          </h2>
          <p className="text-lg md:text-xl opacity-95 max-w-2xl mx-auto mb-12 leading-relaxed text-pretty">
            {ctaFinalDesc}
          </p>
          <Link to="/cadastro" className="inline-flex items-center gap-2 bg-white px-9 py-4 rounded-full font-medium hover:bg-[color:var(--bg)] transition-all duration-500 shadow-2xl hover:shadow-[0_20px_40px_-10px_rgba(0,0,0,0.3)] hover:-translate-y-1 text-sm tracking-wide" style={{ color: "var(--copper)" }} data-testid="final-cta">
            Entrar para a comunidade <ArrowRight size={16} />
          </Link>

          <div className="mt-14 divider-center text-xs uppercase tracking-[0.32em] opacity-80">
            <span>Eu sou a CEO da minha vida</span>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
